import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
  InputBase,
  Collapse,
  Tooltip,
  alpha,
} from "@mui/material";
import {
  SendOutlined,
  NotificationsActiveOutlined,
  SystemUpdate,
  IntegrationInstructions,
  BusinessCenter,
  Inventory2Outlined,
  CategoryOutlined,
  MailOutline,
  ChatBubbleOutline,
  SettingsOutlined,
  ConfirmationNumber,
  ChevronLeft,
  HomeOutlined,
  ShoppingCartOutlined,
  Groups2Outlined,
  ReceiptLongOutlined,
  PublicOutlined,
  PointOfSaleOutlined,
  TodayOutlined,
  CalendarMonthOutlined,
  AdminPanelSettingsOutlined,
  TrendingUpOutlined,
  PieChartOutlined,
  AssessmentOutlined,
  LoyaltyOutlined,
  StorefrontOutlined,
  Menu as MenuIcon,
  Search,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import FlexBetween from "./FlexBetween";
import profileImage from "assets/profile.jpeg";
import brandLogo from "assets/brand-logo.png";
import { canAccess } from "config/roles";

// Curated navigation — every entry resolves to a real, existing route.
// Section headers have `header: true`. Items use `to`; groups use `subItems`.
const navItems = [
  { text: "Dashboard", icon: <HomeOutlined />, to: "/dashboard" },
  { text: "POS", icon: <PointOfSaleOutlined />, to: "/pos" },

  { text: "Sales", header: true },
  { text: "Orders", icon: <ShoppingCartOutlined />, to: "/orders" },
  { text: "Refund Requests", icon: <LoyaltyOutlined />, to: "/refund-requests" },
  { text: "Transactions", icon: <ReceiptLongOutlined />, to: "/transactions" },

  { text: "Catalog", header: true },
  {
    text: "Products",
    icon: <Inventory2Outlined />,
    subItems: [
      { text: "All Products", to: "/products" },
      { text: "Add Product", to: "/products/add-new" },
      { text: "Bulk Import", to: "/products/bulk-import" },
      { text: "Restock Requests", to: "/products/request-restock-list" },
    ],
  },
  { text: "Categories", icon: <CategoryOutlined />, to: "/categories" },
  {
    text: "Brands",
    icon: <StorefrontOutlined />,
    subItems: [
      { text: "Brand List", to: "/brands/list" },
      { text: "Add Brand", to: "/brands/add-new" },
    ],
  },

  { text: "People", header: true },
  { text: "Customers", icon: <Groups2Outlined />, to: "/customers" },
  { text: "Team", icon: <AdminPanelSettingsOutlined />, to: "/admin" },
  { text: "Performance", icon: <TrendingUpOutlined />, to: "/performance" },

  { text: "Analytics", header: true },
  { text: "Reports", icon: <AssessmentOutlined />, to: "/reports" },
  { text: "Overview", icon: <PointOfSaleOutlined />, to: "/overview" },
  { text: "Daily", icon: <TodayOutlined />, to: "/daily" },
  { text: "Monthly", icon: <CalendarMonthOutlined />, to: "/monthly" },
  { text: "Breakdown", icon: <PieChartOutlined />, to: "/breakdown" },
  { text: "Geography", icon: <PublicOutlined />, to: "/geography" },

  { text: "Support", header: true },
  { text: "Inbox", icon: <MailOutline />, to: "/inbox" },
  { text: "Messages", icon: <ChatBubbleOutline />, to: "/messages" },
  { text: "Support Tickets", icon: <ConfirmationNumber />, to: "/Support-Ticket" },

  { text: "Settings", header: true },
  {
    text: "Notifications",
    icon: <NotificationsActiveOutlined />,
    subItems: [
      { text: "Send Notification", icon: <SendOutlined />, to: "/send-notification" },
      { text: "Push Setup", icon: <SettingsOutlined />, to: "/push-notification-setup" },
    ],
  },
  {
    text: "Business Setup",
    icon: <BusinessCenter />,
    subItems: [
      { text: "Business Settings", to: "/system/bussiness-settings" },
      { text: "In-House Shop", to: "/system/in-house-shop" },
      { text: "SEO Settings", to: "/system/seo-settings" },
    ],
  },
  { text: "System Setup", icon: <SystemUpdate />, to: "/system-setup" },
  { text: "Payment Gateways", icon: <IntegrationInstructions />, to: "/third-party" },
];

const FULL_WIDTH = 250;
const RAIL_WIDTH = 76;

const Sidebar = ({
  user,
  effectiveRole,
  isSidebarOpen,
  setIsSidebarOpen,
  isNonMobile,
}) => {
  const { pathname } = useLocation();
  const [active, setActive] = useState(pathname);
  const [openGroups, setOpenGroups] = useState({});
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();

  // On desktop the sidebar never disappears — it collapses to an icon rail.
  const collapsed = isNonMobile && !isSidebarOpen;
  const width = collapsed ? RAIL_WIDTH : FULL_WIDTH;

  useEffect(() => {
    setActive(pathname);
    navItems.forEach((item) => {
      if (item.subItems?.some((s) => s.to === pathname)) {
        setOpenGroups((prev) => ({ ...prev, [item.text]: true }));
      }
    });
  }, [pathname]);

  const isActive = (to) => active?.toLowerCase() === to?.toLowerCase();

  const q = search.trim().toLowerCase();
  const filteredNavItems = navItems
    // 1) Role filter: only show what the effective role can open.
    .map((item) => {
      if (item.header) return item;
      if (item.subItems) {
        const subs = item.subItems.filter((s) => canAccess(effectiveRole, s.to));
        return subs.length ? { ...item, subItems: subs } : null;
      }
      return canAccess(effectiveRole, item.to) ? item : null;
    })
    .filter(Boolean)
    // Drop section headers left with no items after the role filter.
    .filter((item, i, arr) => !item.header || (arr[i + 1] && !arr[i + 1].header))
    // 2) Search filter.
    .map((item) => {
      if (!q) return item;
      if (item.header) return item;
      const matches = item.text.toLowerCase().includes(q);
      const subs = item.subItems?.filter((s) =>
        s.text.toLowerCase().includes(q)
      );
      if (matches) return item;
      if (subs?.length) return { ...item, subItems: subs };
      return null;
    })
    .filter(Boolean);

  const activeBg = alpha(theme.palette.primary.main, 0.16);
  const activeColor = theme.palette.primary.main;

  const go = (to) => {
    if (!to) return;
    navigate(to);
    setActive(to);
    if (!isNonMobile) setIsSidebarOpen(false);
  };

  const handleItemClick = (item) => {
    if (item.subItems) {
      if (collapsed) {
        // No room for a sub-tree on the rail — jump to the first child.
        go(item.subItems[0]?.to);
      } else {
        setOpenGroups((p) => ({ ...p, [item.text]: !p[item.text] }));
      }
    } else {
      go(item.to);
    }
  };

  const renderItem = (item) => {
    if (item.header) {
      if (collapsed) return <Divider key={item.text} sx={{ my: 1, mx: 1.5 }} />;
      return (
        <Typography
          key={item.text}
          variant="caption"
          sx={{
            display: "block",
            m: "1rem 0 0.25rem 1rem",
            color: "text.secondary",
            textTransform: "uppercase",
            letterSpacing: 0.6,
            fontWeight: 700,
          }}
        >
          {item.text}
        </Typography>
      );
    }

    const { text, icon, to, subItems } = item;
    const groupOpen = (!!openGroups[text] || !!q) && !collapsed;
    const activeSelf = isActive(to) || (subItems && subItems.some((s) => isActive(s.to)));

    const button = (
      <ListItemButton
        onClick={() => handleItemClick(item)}
        sx={{
          borderRadius: "8px",
          justifyContent: collapsed ? "center" : "flex-start",
          px: collapsed ? 1 : 2,
          backgroundColor: activeSelf ? activeBg : "transparent",
          color: activeSelf ? activeColor : "text.primary",
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 38,
            justifyContent: "center",
            color: activeSelf ? activeColor : "text.secondary",
          }}
        >
          {icon}
        </ListItemIcon>
        {!collapsed && (
          <>
            <ListItemText
              primary={text}
              primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
            />
            {subItems && (groupOpen ? <ExpandLess /> : <ExpandMore />)}
          </>
        )}
      </ListItemButton>
    );

    return (
      <React.Fragment key={text}>
        <ListItem disablePadding sx={{ mb: 0.25 }}>
          {collapsed ? (
            <Tooltip title={text} placement="right" arrow>
              {button}
            </Tooltip>
          ) : (
            button
          )}
        </ListItem>

        {subItems && !collapsed && (
          <Collapse in={groupOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {subItems.map((s) => (
                <ListItem key={s.text} disablePadding sx={{ pl: 2 }}>
                  <ListItemButton
                    onClick={() => go(s.to)}
                    sx={{
                      borderRadius: "8px",
                      py: 0.5,
                      backgroundColor: isActive(s.to) ? activeBg : "transparent",
                      color: isActive(s.to) ? activeColor : "text.secondary",
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    <ListItemText
                      primary={s.text}
                      primaryTypographyProps={{ fontSize: 13 }}
                      sx={{ pl: 3 }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawerContent = (
    <>
      <Box>
        <Box m={collapsed ? "1.25rem 0" : "1.25rem 1.5rem 0.5rem 1.5rem"}>
          <FlexBetween sx={{ justifyContent: collapsed ? "center" : "space-between" }}>
            <Box
              component="img"
              src={brandLogo}
              alt="BharatStore"
              sx={{
                height: collapsed ? 40 : 44,
                width: collapsed ? 40 : 44,
                borderRadius: 2,
                objectFit: "cover",
              }}
            />
            {!collapsed && (
              <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                {isNonMobile ? <MenuIcon /> : <ChevronLeft />}
              </IconButton>
            )}
          </FlexBetween>
        </Box>

        {!collapsed && (
          <Box m="0.75rem 1rem">
            <FlexBetween
              sx={{
                backgroundColor: theme.palette.background.secondary,
                borderRadius: "9px",
                p: "0.1rem 0.5rem",
              }}
            >
              <InputBase
                placeholder="Search menu…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ flexGrow: 1, fontSize: 14 }}
              />
              <IconButton size="small">
                <Search fontSize="small" />
              </IconButton>
            </FlexBetween>
          </Box>
        )}

        <List sx={{ px: 1 }}>{filteredNavItems.map(renderItem)}</List>
      </Box>

      {!collapsed && (
        <Box mt="auto">
          <Divider />
          <FlexBetween gap="1rem" m="1rem 1.5rem">
            <Box
              component="img"
              alt="profile"
              src={profileImage}
              height="42px"
              width="42px"
              borderRadius="50%"
              sx={{ objectFit: "cover" }}
            />
            <Box textAlign="left" flexGrow={1} overflow="hidden">
              <Typography fontWeight="bold" fontSize="0.85rem" color="text.primary" noWrap>
                {user?.name || "—"}
              </Typography>
              <Typography fontSize="0.75rem" color="text.secondary" noWrap>
                {user?.role || user?.occupation || ""}
              </Typography>
            </Box>
            <SettingsOutlined sx={{ color: "text.secondary", fontSize: 20 }} />
          </FlexBetween>
          <Divider />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", m: "10px 0 12px 20px" }}
          >
            Powered by Alokknight.com · © 2024
          </Typography>
        </Box>
      )}
    </>
  );

  const paperSx = {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    boxSizing: "border-box",
    width,
    overflowX: "hidden",
    display: "flex",
    flexDirection: "column",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  };

  // Desktop: permanent icon-rail/full drawer that never vanishes.
  if (isNonMobile) {
    return (
      <Drawer
        variant="permanent"
        open
        sx={{
          width,
          flexShrink: 0,
          transition: theme.transitions.create("width"),
          "& .MuiDrawer-paper": paperSx,
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // Mobile: temporary overlay drawer.
  return (
    <Drawer
      variant="temporary"
      open={isSidebarOpen}
      onClose={() => setIsSidebarOpen(false)}
      ModalProps={{ keepMounted: true }}
      sx={{ "& .MuiDrawer-paper": { ...paperSx, width: FULL_WIDTH } }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
